import React, { useContext, useMemo } from 'react'
import { useQuery } from '@apollo/client'
import UserContext from '../context/UserContext'
import { GET_TASKS_QUERY, GET_NOTES_QUERY, LIST_SHOOTS_QUERY } from '../query/gqlQueries'
import styles from './HomeView.module.scss'
import d from './Dashboard.module.scss'
import { openKanbanOn } from './kanbanHandoff'

// Work's home, in the Apple TV look: the task to do now big at the top, the
// counts that matter, then shelves of large cards — in progress, due soon,
// projects, notes, shoots — and the tools last. Everything is the signed-in
// person's own (the server scopes each query to them).

const OPEN = new Set(['TODO', 'INPROGRESS', 'VALIDATING', 'BACKLOG'])
const PRIORITY = { urgent: 4, high: 3, medium: 2, med: 2, low: 1 }
const STATUS_NAME = { BACKLOG: 'Backlog', TODO: 'To do', INPROGRESS: 'In progress', VALIDATING: 'Checking', DONE: 'Done', CANCELLED: 'Cancelled' }

const DAY = 24 * 60 * 60 * 1000
const startOfToday = () => { const t = new Date(); t.setHours(0, 0, 0, 0); return t.getTime() }
const dueOf = (t) => { const v = t.properties?.dueDate; const ms = v ? new Date(v).getTime() : NaN; return isNaN(ms) ? null : ms }
const prio = (t) => PRIORITY[String(t.properties?.priority || '').toLowerCase()] || 0
const dayLabel = (ms) => {
  if (ms == null) return ''
  const days = Math.round((ms - startOfToday()) / DAY)
  if (days < -1) return `${-days} days overdue`
  if (days === -1) return 'Overdue since yesterday'
  if (days === 0) return 'Due today'
  if (days === 1) return 'Due tomorrow'
  if (days < 7) return `Due ${new Date(ms).toLocaleDateString(undefined, { weekday: 'long' })}`
  return `Due ${new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`
}
const when = (v) => { const t = v ? new Date(v) : null; return t && !isNaN(t) ? t.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '' }

/** The one to do now: in progress before to do, then priority, then the soonest due. */
const rank = (t) => [t.status === 'INPROGRESS' ? 0 : t.status === 'TODO' ? 1 : t.status === 'VALIDATING' ? 2 : 3, -prio(t), dueOf(t) ?? Infinity]
const byRank = (a, b) => { const x = rank(a), y = rank(b); for (let i = 0; i < x.length; i++) if (x[i] !== y[i]) return x[i] - y[i]; return 0 }

export default function WorkHome({ run }) {
  const { username } = useContext(UserContext)
  const tasksQ = useQuery(GET_TASKS_QUERY, { variables: { username }, skip: !username, fetchPolicy: 'cache-and-network', errorPolicy: 'ignore' })
  const notesQ = useQuery(GET_NOTES_QUERY, { variables: { username }, skip: !username, fetchPolicy: 'cache-and-network', errorPolicy: 'ignore' })
  const shootsQ = useQuery(LIST_SHOOTS_QUERY, { variables: {}, skip: !username, fetchPolicy: 'cache-and-network', errorPolicy: 'ignore' })

  const tasks = useMemo(() => tasksQ.data?.getTasks || [], [tasksQ.data])
  const open = useMemo(() => tasks.filter(t => OPEN.has(t.status || 'TODO')).sort(byRank), [tasks])
  const today = startOfToday()
  const inProgress = open.filter(t => t.status === 'INPROGRESS')
  const toDo = open.filter(t => (t.status || 'TODO') === 'TODO')
  const checking = open.filter(t => t.status === 'VALIDATING')
  const dueSoon = open.filter(t => { const d = dueOf(t); return d != null && d < today + 14 * DAY }).sort((a, b) => dueOf(a) - dueOf(b))
  const overdue = dueSoon.filter(t => dueOf(t) < today)
  const thisWeek = dueSoon.filter(t => dueOf(t) < today + 7 * DAY)
  const next = open[0]

  const projects = useMemo(() => {
    const by = new Map()
    for (const t of tasks) {
      const p = t.properties?.project
      if (!p) continue
      const e = by.get(p) || { name: p, total: 0, done: 0, open: 0, active: 0 }
      if (t.status === 'CANCELLED') { by.set(p, e); continue }
      e.total += 1
      if (t.status === 'DONE') e.done += 1; else e.open += 1
      if (t.status === 'INPROGRESS') e.active += 1
      by.set(p, e)
    }
    return [...by.values()].filter(p => p.total).sort((a, b) => b.active - a.active || b.open - a.open).slice(0, 10)
  }, [tasks])

  const notes = useMemo(() => [...(notesQ.data?.getNotes || [])]
    .sort((a, b) => String(b.properties?.dateLastModified || '').localeCompare(String(a.properties?.dateLastModified || '')))
    .slice(0, 10), [notesQ.data])
  const shoots = useMemo(() => [...(shootsQ.data?.listShoots || [])]
    .sort((a, b) => String(b.properties?.shootDate || b.properties?.createdAt || '').localeCompare(String(a.properties?.shootDate || a.properties?.createdAt || '')))
    .slice(0, 10), [shootsQ.data])

  const loading = tasksQ.loading && !tasksQ.data

  const taskCard = (t) => {
    const due = dueOf(t)
    const late = due != null && due < today
    const spent = Number(t.properties?.timeSpent) || 0
    const budget = Number(t.properties?.timeBudget) || 0
    return (
      <button key={t.identity} className={d.tile} onClick={() => run('taskBoard')}>
        <span className={d.tileTop}>
          <span className={d.tag}>{STATUS_NAME[t.status] || 'To do'}</span>
          {prio(t) >= 3 && <span className={`${d.tag} ${d.tagHot}`}>{t.properties.priority}</span>}
        </span>
        <span className={d.tileTitle}>{t.properties?.title || 'Untitled task'}</span>
        <span className={d.tileMeta}>{[t.properties?.project, due != null ? dayLabel(due) : null].filter(Boolean).join(' · ')}</span>
        {budget > 0 && <span className={d.meter}><span style={{ width: `${Math.min(100, (spent / budget) * 100)}%` }} className={spent > budget ? d.meterOver : ''} /></span>}
        {late && <span className={d.lateDot} aria-label="Overdue" />}
      </button>
    )
  }

  return (
    <>
      {/* ── the one to do now ── */}
      <section className={`${styles.card} ${d.hero}`}>
        <div className={styles.cardHead}><span className={styles.cardLabel}>Up next</span>
          <button className={styles.linkBtn} onClick={() => run('kanban')}>Kanban →</button></div>
        {loading ? <div className={styles.muted}>Loading your tasks…</div>
          : !next ? (
            <div className={d.heroBody}>
              <div><div className={d.heroTitle}>Nothing open — all clear.</div><div className={d.heroMeta}>Add a task, or import a list from a document.</div></div>
              <button className={d.primary} onClick={() => run('addTask')}>New task</button>
            </div>
          ) : (
            <div className={d.heroBody}>
              <div>
                <div className={d.heroTitle}>{next.properties?.title}</div>
                <div className={d.heroMeta}>{[STATUS_NAME[next.status] || 'To do', next.properties?.project, dueOf(next) != null ? dayLabel(dueOf(next)) : null, next.properties?.priority].filter(Boolean).join(' · ')}</div>
                {next.properties?.description && <div className={d.heroText}>{next.properties.description}</div>}
              </div>
              <button className={d.primary} onClick={() => run('taskBoard')}>Open the board →</button>
            </div>
          )}
      </section>

      {/* ── the counts that matter ── */}
      <div className={d.stats}>
        <button className={d.stat} onClick={() => run('taskBoard')}><b>{inProgress.length}</b><span>In progress</span></button>
        <button className={d.stat} onClick={() => run('taskBoard')}><b>{toDo.length}</b><span>To do</span></button>
        <button className={`${d.stat} ${overdue.length ? d.statHot : ''}`} onClick={() => run('taskBoard')}>
          <b>{thisWeek.length}</b><span>{overdue.length ? `Due this week · ${overdue.length} overdue` : 'Due this week'}</span></button>
        <button className={d.stat} onClick={() => run('kanban')}><b>{checking.length}</b><span>Being checked</span></button>
      </div>

      {inProgress.length > 0 && (
        <Shelf title="In progress">{inProgress.slice(0, 12).map(taskCard)}</Shelf>
      )}
      {dueSoon.length > 0 && (
        <Shelf title="Due soon">{dueSoon.slice(0, 12).map(taskCard)}</Shelf>
      )}
      {projects.length > 0 && (
        <Shelf title="Projects">
          {projects.map(p => (
            <button key={p.name} className={d.tile} onClick={() => openKanbanOn(p.name, () => run('kanban'))}>
              <span className={d.tileTitle}>{p.name}</span>
              <span className={d.tileMeta}>{p.open} open{p.active ? ` · ${p.active} in progress` : ''} · {p.done} done</span>
              <span className={d.meter}><span className={d.meterDone} style={{ width: `${Math.round((p.done / p.total) * 100)}%` }} /></span>
            </button>
          ))}
        </Shelf>
      )}
      {notes.length > 0 && (
        <Shelf title="Notes" action={{ label: 'All notes →', onClick: () => run('notes') }}>
          {notes.map(n => (
            <button key={n.identity} className={d.tile} onClick={() => run('notes')}>
              <span className={d.tileTitle}>{n.properties?.title || 'Untitled note'}</span>
              <span className={d.tileMeta}>{[n.properties?.linkedProject, when(n.properties?.dateLastModified || n.properties?.dateAdded)].filter(Boolean).join(' · ')}</span>
            </button>
          ))}
        </Shelf>
      )}
      {shoots.length > 0 && (
        <Shelf title="Shoots" action={{ label: 'Media →', onClick: () => run('media') }}>
          {shoots.map(sh => (
            <button key={sh.identity} className={d.tile} onClick={() => run('media')}>
              <span className={d.tileTitle}>{sh.properties?.name}</span>
              <span className={d.tileMeta}>{[sh.properties?.project, when(sh.properties?.shootDate || sh.properties?.createdAt), sh.properties?.assetCount != null ? `${sh.properties.assetCount} files` : null].filter(Boolean).join(' · ')}</span>
              {sh.properties?.checkedOutBy && <span className={d.tag}>With {sh.properties.checkedOutBy}</span>}
            </button>
          ))}
        </Shelf>
      )}
    </>
  )
}

/** A row of large cards, scrolling sideways when there are more — the TV's shelf. */
export function Shelf({ title, action, children }) {
  return (
    <section className={d.shelf}>
      <div className={d.shelfHead}>
        <span className={styles.cardLabel}>{title}</span>
        {action && <button className={styles.linkBtn} onClick={action.onClick}>{action.label}</button>}
      </div>
      <div className={d.shelfRow}>{children}</div>
    </section>
  )
}
